// Weekly timetables → today's sessions, created automatically (FR-SES-2).
// Session ids are derived from (template, date) so two devices creating the
// same session offline produce the same row instead of a duplicate.
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import { nowIso } from '@/lib/db/db';
import { todayLocal } from './repo';

export async function deterministicId(...parts: string[]): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts.join(':'))));
  digest[6] = (digest[6]! & 0x0f) | 0x50; // version 5 style
  digest[8] = (digest[8]! & 0x3f) | 0x80; // RFC 4122 variant
  const h = Array.from(digest.subarray(0, 16), (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
}

/** Create today's planned sessions from active templates. Safe to call often. */
export async function materializeTodaySessions(db: AbstractPowerSyncDatabase, clinicId: string, userId: string | null, deviceId: string | null): Promise<number> {
  const date = todayLocal();
  const weekday = new Date(date + 'T12:00:00').getDay(); // 0 = Sunday, matches session_templates.weekday
  const templates = await db.getAll<{ id: string; doctor_id: string; start_time: string; end_time: string; room: string | null; token_cap: number | null }>(
    `select t.id, t.doctor_id, t.start_time, t.end_time, t.room, t.token_cap from session_templates t join doctors d on d.id = t.doctor_id
     where t.clinic_id = ? and t.active = 1 and d.active = 1 and t.weekday = ?`, [clinicId, weekday]);
  let created = 0;
  for (const tpl of templates) {
    const exists = await db.getOptional('select 1 from sessions where doctor_id = ? and session_date = ? and start_time = ?', [tpl.doctor_id, date, tpl.start_time]);
    if (exists) continue;
    const id = await deterministicId(tpl.id, date);
    const ts = nowIso();
    await db.execute(
      `insert or ignore into sessions (id, clinic_id, doctor_id, template_id, session_date, start_time, end_time, room, token_cap, status, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, ?, 'planned', ?, ?, ?, ?, ?)`,
      [id, clinicId, tpl.doctor_id, tpl.id, date, tpl.start_time, tpl.end_time, tpl.room, tpl.token_cap, ts, userId, ts, userId, deviceId]);
    created++;
  }
  return created;
}
